import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../axiosInstance";

function niceDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function priorityClass(priority) {
  const value = String(priority || "NORMAL").toUpperCase();
  if (value === "URGENT") return "text-bg-danger";
  if (value === "IMPORTANT") return "text-bg-warning";
  return "text-bg-light border";
}

function SchoolCommunicationsPage() {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axiosInstance.get("communications/feed/");
      setCommunications(response.data?.communications || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Unable to load school communications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return communications;
    return communications.filter((item) =>
      [item.title, item.message, item.created_by, item.source, item.attachment_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [communications, search]);

  const renderMedia = (item) => {
    if (!item.attachment_url) return null;

    if (item.media_type === "IMAGE") {
      return (
        <div className="mt-3">
          <a href={item.attachment_url} target="_blank" rel="noreferrer">
            <img
              src={item.attachment_url}
              alt={item.attachment_name || item.title}
              className="img-fluid rounded-3 border"
              style={{ maxHeight: 420, objectFit: "contain" }}
            />
          </a>
        </div>
      );
    }

    if (item.media_type === "VIDEO") {
      return (
        <div className="mt-3">
          <video
            controls
            preload="metadata"
            className="w-100 rounded-3 border"
            style={{ maxHeight: 430 }}
          >
            <source src={item.attachment_url} />
            Your browser does not support this video.
          </video>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <div className="text-success fw-semibold small text-uppercase mb-1">
            School Communication
          </div>
          <h2 className="fw-bold mb-1">Newsletters & Communications</h2>
          <p className="text-muted mb-0">
            Messages, newsletters and media shared with your account by the school.
          </p>
        </div>
        <button type="button" className="btn btn-light border" onClick={load}>
          <i className="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="input-group">
            <span className="input-group-text bg-white">
              <i className="bi bi-search"></i>
            </span>
            <input
              className="form-control"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search newsletters and school messages..."
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body py-5 text-center text-muted">
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            Loading school communications...
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body py-5 text-center">
            <i className="bi bi-megaphone fs-1 text-muted"></i>
            <h5 className="fw-bold mt-3">No communications yet</h5>
            <p className="text-muted mb-0">
              School newsletters and messages intended for you will appear here.
            </p>
          </div>
        </div>
      ) : (
        <div className="row g-4">
          {filtered.map((item) => (
            <div className="col-12" key={item.id}>
              <article className="card border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                    <div>
                      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                        <span className={`badge ${priorityClass(item.priority)}`}>
                          {item.priority || "NORMAL"}
                        </span>
                        <span className="badge text-bg-light border">
                          {item.source || "School Communication"}
                        </span>
                      </div>
                      <h4 className="fw-bold mb-1">{item.title}</h4>
                      <div className="text-muted small">
                        {item.created_by || "The Grange Christian School"}
                        {" · "}
                        {niceDate(item.sent_at || item.created_at || item.scheduled_for)}
                      </div>
                    </div>
                  </div>

                  <p className="mt-3 mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {item.message}
                  </p>

                  {renderMedia(item)}

                  {item.attachment_url && (
                    <div className="mt-3">
                      <a
                        href={item.attachment_url}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-outline-success"
                      >
                        <i className="bi bi-paperclip me-2"></i>
                        {item.media_type === "IMAGE" || item.media_type === "VIDEO"
                          ? "Open media"
                          : item.attachment_name || "Open attachment"}
                      </a>
                      {item.attachment_name && (
                        <small className="text-muted ms-3">{item.attachment_name}</small>
                      )}
                    </div>
                  )}
                </div>
              </article>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SchoolCommunicationsPage;
